/**
 * useAssetLibrary - the signed-in account's asset library, live.
 *
 * Watches the two small collections (folders, asset rows) while mounted.
 * When the library first arrives it empties the Trash of anything past its
 * 30 days and takes the leftovers of saves that never finished - on
 * Firebase, which runs no server code, this IS the Trash's clock; on
 * PocketBase the daily cron does it as well, and doing it here too keeps
 * both backends behaving alike. A failure is logged, never swallowed.
 */
import { useEffect, useRef, useState } from 'react';
import { watchLibrary, deleteForever, reapOrphans, type Library } from '../assetStore';
import { expired } from '../engine/assets/trash';
import { diag, errorSummary } from '../diagnostics/log';

export interface LibraryState {
  lib: Library | null;
  error: string | null;
}

/**
 * Delete for good what has waited out its 30 days in the Trash, and take the
 * parts of saves that never finished. Such a save leaves its mark on the row:
 * a number reserved past the last whole version - so only those assets have
 * their parts listed, never the whole library.
 */
async function tidy(uid: string, lib: Library, now: number): Promise<void> {
  for (const a of lib.assets.filter((x) => expired(x, now))) await deleteForever(uid, a.id);
  for (const a of lib.assets.filter((x) => !x.deletedAt && x.nextVersion - 1 > x.version)) {
    await reapOrphans(uid, a.id, now);
  }
}

export default function useAssetLibrary(uid: string | null): LibraryState {
  const [state, setState] = useState<LibraryState>({ lib: null, error: null });
  const purged = useRef(false);
  useEffect(() => {
    if (!uid) return undefined;
    purged.current = false;
    return watchLibrary(
      uid,
      (lib) => {
        setState({ lib, error: null });
        if (purged.current) return;
        purged.current = true;
        tidy(uid, lib, Date.now()).catch((err) =>
          diag.error('assets', 'tidying the library failed', errorSummary(err)),
        );
      },
      (err) => {
        diag.error('assets', 'the library would not load', errorSummary(err));
        setState((s) => ({ ...s, error: 'Your library could not be loaded. Check the connection and reload.' }));
      },
    );
  }, [uid]);
  return state;
}
